/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs';
import memorize from 'lodash/memoize';
import { z } from 'zod';

import { generatePath } from 'Components/shared/Wizard/Router.helpers';
import { validatePhone } from 'Services/api/miscellaneous';
import ROUTES from 'Utilities/routes';

import { MOBILE_PHONE_PREFIX, VALIDATION_ERROR } from './AccountSettingsCompanyInformation.consts';
import type {
	CompanyInformationAvailableRoutes,
	CompanyInformationRoutePath,
	FormFieldValidator,
} from './AccountSettingsCompanyInformation.types';

const validationSchema = {
	email: z.string().email(),
	string: z.string(),
};

export const KYC_BASE_PATH = '/account-settings/kyc';

export const createRoute = <P extends CompanyInformationRoutePath>(path: P, prefix?: string): string => {
	const baseRoute = prefix ?? ROUTES.COMPANY_INFORMATION.href;

	return generatePath<CompanyInformationAvailableRoutes<P>>(prefix ? `${baseRoute}/[[...slug]]` : baseRoute, {
		slug: path ? [path] : [],
	});
};

export const getCompanyInformationSectionPaths = (prefix?: string) => ({
	AUTHORISED_USERS: createRoute('authorised-users', prefix),
	BENEFICIAL_OWNERS: createRoute('beneficial-owners', prefix),
	COMPANY_DETAILS: createRoute('company-details', prefix),
	COMPANY_INFORMATION: createRoute('', prefix),
	DIRECTORS: createRoute('directors', prefix),
	KYC_COMPLETE: createRoute('kyc-complete', prefix),
	KYC_COMPLETE_PRO: createRoute('kyc-complete-live-pro', prefix),
	SUMMARY: createRoute('summary', prefix),
});

export const formFieldFormatter = {
	alphabetic: (value?: string): string => {
		const digitsRegex = /\d/g;
		return !!value && validationSchema.string.safeParse(value)?.success
			? value.replace(digitsRegex, '')
			: (value ?? '');
	},
	date: (value?: string): string => {
		const slashRegex = /\//g;
		return value && validationSchema.string.safeParse(value)?.success ? value.replace(slashRegex, '-') : (value ?? '');
	},
	phoneNumber: (value?: string): string => {
		const notDigitsNorPlusSign = /[^\d.+]/g;
		return value && validationSchema.string.safeParse(value)?.success
			? value.replace(notDigitsNorPlusSign, '')
			: (value ?? '');
	},
	phoneWithPrefix: (value?: string): string => {
		return value?.startsWith(MOBILE_PHONE_PREFIX) ? value : `${MOBILE_PHONE_PREFIX} ${value}`;
	},
};

const isValidDate = (value: string): boolean => {
	if (!value) {
		return false;
	}

	const date = dayjs(value, ['DD-MM-YYYY', 'DD/MM/YYYY'], undefined, true);
	return date?.isValid();
};

const isPastDate = (value: string): boolean => {
	if (!value || !isValidDate(value)) {
		return false;
	}

	const date = dayjs(value, ['DD-MM-YYYY', 'DD/MM/YYYY'], undefined, true);
	return date?.isBefore(dayjs());
};

export const emailValidator: FormFieldValidator = memorize((value) => {
	return !!value && !validationSchema.email.safeParse(value)?.success
		? { code: VALIDATION_ERROR.EMAIL_INVALID }
		: undefined;
});

export const dateValidator: FormFieldValidator = memorize((value: string) => {
	return !!value && !isValidDate(value) ? { code: VALIDATION_ERROR.DATE_INVALID } : undefined;
});

export const pastDateValidator: FormFieldValidator = memorize((value) => {
	return !!value && !isPastDate(value) ? { code: VALIDATION_ERROR.DATE_INVALID } : undefined;
});

export const composeValidators =
	(validators: Array<FormFieldValidator>): FormFieldValidator =>
	(value, allValues, meta) => {
		for (const validator of validators) {
			const error = validator(value, allValues, meta);
			if (error) {
				return error;
			}
		}
		return undefined;
	};

export const phoneNumberValidator: FormFieldValidator = memorize(async (value, allValues, meta) => {
	if (!value || !meta?.modified) {
		return undefined;
	}

	try {
		const { valid } = await validatePhone(value);
		return !valid ? { code: VALIDATION_ERROR.PHONE_NUMBER_INVALID } : undefined;
	} catch (e) {
		return { code: VALIDATION_ERROR.PHONE_NUMBER_INVALID };
	}
});
